import { useSyncExternalStore } from "react";
import * as tus from "tus-js-client";

export interface UploadItem {
  id: string;
  orgId: string;
  name: string;
  /** MIME type of the file (may be empty). */
  mime: string;
  size: number;
  uploaded: number;
  status: "uploading" | "done" | "error" | "cancelled";
  error?: string;
  assetId?: string;
  /** Start and latest byte count, for speed and remaining time. */
  startedAt: number;
  finishedAt?: number;
}

type Listener = () => void;

const CHUNK_SIZE = 20 * 1024 * 1024;

let items: UploadItem[] = [];
const listeners = new Set<Listener>();
const running = new Map<string, tus.Upload>();
const pending = new Map<string, () => void>();
const MAX_PARALLEL_UPLOADS = 2;

function pump() {
  while (running.size < MAX_PARALLEL_UPLOADS && pending.size) {
    const [id, start] = pending.entries().next().value!;
    pending.delete(id);
    start();
  }
}
const finishedCallbacks = new Set<(item: UploadItem) => void>();

function emit() {
  items = [...items];
  for (const listener of listeners) listener();
  updateUnloadGuard();
}

// Closing the tab stops running uploads; the browser asks first.
let guarded = false;
const guard = (event: BeforeUnloadEvent) => event.preventDefault();
function updateUnloadGuard() {
  const needed = items.some((item) => item.status === "uploading");
  if (needed === guarded) return;
  guarded = needed;
  if (needed) window.addEventListener("beforeunload", guard);
  else window.removeEventListener("beforeunload", guard);
}

function patch(id: string, update: Partial<UploadItem>) {
  const item = items.find((entry) => entry.id === id);
  if (item) Object.assign(item, update);
  emit();
}

function parseError(error: Error): string {
  const response = (error as tus.DetailedError).originalResponse;
  const body = response?.getBody();
  if (body) {
    try {
      const parsed = JSON.parse(body) as { error?: string };
      if (parsed.error) return parsed.error;
    } catch {
      return body;
    }
  }
  return "Upload fehlgeschlagen";
}

/**
 * Resumable uploads via tus: large videos survive flaky connections and the
 * backend validates type, permissions and quota before accepting any bytes.
 */
export function startUpload(
  file: File,
  options: {
    orgId: string;
    projectId?: string | undefined;
    /** Folder of the media library (omitted/null = top level). */
    folderId?: string | null | undefined;
  },
): string {
  const id = crypto.randomUUID();
  items.unshift({
    id,
    orgId: options.orgId,
    name: file.name,
    mime: file.type,
    size: file.size,
    uploaded: 0,
    status: "uploading",
    startedAt: Date.now(),
  });
  emit();

  const upload = new tus.Upload(file, {
    endpoint: `${window.location.origin}/api/uploads`,
    chunkSize: CHUNK_SIZE,
    retryDelays: [0, 2000, 5000, 10000, 20000],
    removeFingerprintOnSuccess: true,
    metadata: {
      orgId: options.orgId,
      filename: file.name,
      filetype: file.type,
      ...(options.projectId ? { projectId: options.projectId } : {}),
      ...(options.folderId ? { folderId: options.folderId } : {}),
    },
    onShouldRetry: (error) => {
      const status = (error as tus.DetailedError).originalResponse?.getStatus() ?? 0;
      return status === 0 || status >= 500 || status === 409 || status === 423 || status === 429;
    },
    onProgress: (uploaded) => patch(id, { uploaded }),
    onError: (error) => {
      running.delete(id);
      pump();
      patch(id, { status: "error", error: parseError(error), finishedAt: Date.now() });
    },
    onSuccess: ({ lastResponse }) => {
      running.delete(id);
      pump();
      patch(id, {
        status: "done",
        uploaded: file.size,
        assetId: lastResponse.getHeader("X-Asset-Id") ?? undefined,
        finishedAt: Date.now(),
      });
      const item = items.find((entry) => entry.id === id);
      if (item) for (const callback of finishedCallbacks) callback(item);
    },
  });
  pending.set(id, () => {
    running.set(id, upload);
    void upload
      .findPreviousUploads()
      .then((previous) => {
        if (
          running.get(id) !== upload ||
          items.find((item) => item.id === id)?.status !== "uploading"
        )
          return;
        if (previous[0]) upload.resumeFromPreviousUpload(previous[0]);
        upload.start();
      })
      .catch((error: Error) => {
        running.delete(id);
        patch(id, { status: "error", error: parseError(error), finishedAt: Date.now() });
        pump();
      });
  });
  pump();
  return id;
}

export function cancelUpload(id: string) {
  pending.delete(id);
  const upload = running.get(id);
  if (upload) {
    void upload
      .abort(true)
      .catch(() => undefined)
      .finally(() => {
        running.delete(id);
        pump();
      });
  }
  patch(id, { status: "cancelled", finishedAt: Date.now() });
}

export function clearFinishedUploads() {
  items = items.filter((item) => item.status === "uploading");
  emit();
}

export function dismissUpload(id: string) {
  items = items.filter((item) => item.id !== id || item.status === "uploading");
  emit();
}

export function onUploadFinished(callback: (item: UploadItem) => void) {
  finishedCallbacks.add(callback);
  return () => {
    finishedCallbacks.delete(callback);
  };
}

export function useUploads(): UploadItem[] {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => items,
  );
}
